import React from "react";
//Limitless imports
import { useCustomerContext } from "../customer/CustomerContext";
import { useNotificationContext } from "@limitless/common/hooks";
import { MESSAGE_STATE_LOADING, LAST_MESSAGE_KEY, FEEDBACK, EXPERT_FEEDBACK, FURTHER_CONTACT } from "../../utils/customer-message-state-engine/CustomerMessageStateConstants";
import { createCustomerMessage, fetchCustomerMessage, acceptExpertAnswer, submitMessageScore, submitMessageTestimonial, createDialogue, escalateCustomerMessage, reportCustomerMessage, submitNetPromotorScoreNFeedback, secureData, addCustomerAttachment, generateCustomerVerificationCode } from "../../apis/customer-message-api/CustomerMessageAPI";
import { VERIFY, CREATE, DONOTCREATE, CONFIRMATION_REQUIRED_WITH_OPEN_MESSAGE, INVALID_CODE, DO_NOT_CREATE_WITH_OPEN_MESSAGE, VERIFICATION_CODE_REQUIRED, VERIFICATION_CODE_REQUIRED_OPEN_MESSAGE } from "@limitless/libraries/src/portal/utils/ReContactMessageConstant";
import { calculatePublicMessageState } from "../../utils/customer-message-state-engine/CustomerMessageStateEngine";
import { useCustomerHistoryContext } from "../customer-history/CustomerHistoryContext";
import { ClientContext } from "@limitless/common/utils";
const INITIAL_STATE = {
  loading: false,
  typing: false,
  dialogues: [],
  footerState: MESSAGE_STATE_LOADING,
  expertAuthors: {},
  custScoreScheme: {
    name: "3-faces",
    maxScore: 3,
    resolvedThreshold: 3
  },
  hasError: false,
  isAccessDenied: false,
  isNPSEnabled: false
};
export const useCustomerMessage = () => {
  const [message, setMessage] = React.useState(INITIAL_STATE);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [isMessageOpen, setIsMessageOpen] = React.useState(true);
  const {
    updateHistoryMessage,
    addMessageToHistory
  } = useCustomerHistoryContext();
  const {
    updateCustomerEmailHashAndClearQuestion,
    getCustomerData,
    updateCustomerKey,
    customer
  } = useCustomerContext();
  const {
    addFailureNotification
  } = useNotificationContext();
  const [previewImage, setPreviewImage] = React.useState(null);
  const [reContactMessageCreationError, setReContactMessageCreationError] = React.useState("");
  const updateMessage = messageProps => {
    const newMessage = {
      ...message,
      ...messageProps
    };
    setMessage(newMessage);
  };
  const setLastViewMessage = ({
    messageId,
    accessKey
  }) => {
    if (!ClientContext._clearSessionMessage) {
      sessionStorage.setItem(LAST_MESSAGE_KEY, JSON.stringify({
        messageId,
        accessKey
      }));
    }
  };
  const createMessage = (customer, createCallback) => {
    setMessage({
      ...INITIAL_STATE,
      loading: true
    });
    createCustomerMessage(customer, result => {
      if (!result.hasError) {
        fetchMessage(result.messageId, result.accessKey);
        updateCustomerEmailHashAndClearQuestion(result.customerEmailHash);
        setLastViewMessage(result);
        createCallback(result);
      } else {
        setMessage({
          ...message,
          loading: false
        });
        if (result.message === INVALID_CODE) {
          setReContactMessageCreationError("The verification code is not valid. Please try again or ");
        } else if (result.message === CONFIRMATION_REQUIRED_WITH_OPEN_MESSAGE) {
          createCallback(CONFIRMATION_REQUIRED_WITH_OPEN_MESSAGE);
        } else if (result.message === DO_NOT_CREATE_WITH_OPEN_MESSAGE) {
          createCallback(DO_NOT_CREATE_WITH_OPEN_MESSAGE);
        } else if (result.message === VERIFICATION_CODE_REQUIRED_OPEN_MESSAGE) {
          createCallback(VERIFICATION_CODE_REQUIRED_OPEN_MESSAGE);
        } else if (result.message === VERIFICATION_CODE_REQUIRED) {
          createCallback(VERIFICATION_CODE_REQUIRED);
        } else {
          addFailureNotification("Unable to create message");
        }
      }
    });
  };
  const generateVerificationCode = (customer, groupName, createCallback) => {
    setMessage({
      ...INITIAL_STATE,
      loading: true
    });
    generateCustomerVerificationCode(customer, groupName, result => {
      setMessage({
        ...message,
        loading: false
      });
      if (typeof createCallback === "function") {
        if (!result.hasError) {
          createCallback(result);
        } else {
          addFailureNotification("Incorrect verification code");
        }
      }
    });
  };
  const processMessageBasedOnRule = (reContactMessageRule, newMessageRule, createCallback) => {
    const {
      name,
      email
    } = customer;
    const openDialog = () => setDialogOpen(true);
    const updateMessageState = () => {
      setIsMessageOpen(false);
    };

    // Function to handle verification action, e.g., sending a verification code
    const verifyAction = () => {
      const payload = {
        customerFirstName: name === null || name === void 0 ? void 0 : name.value,
        email: email === null || email === void 0 ? void 0 : email.value
      };
      openDialog();
      generateVerificationCode(payload, message === null || message === void 0 ? void 0 : message.groupName, result => result && openDialog());
    };

    // Function to create a message based on customer data
    const createMessageAction = () => {
      createMessage(getCustomerData(), response => {
        // If the response indicates confirmation is required or message should not be created
        if ([CONFIRMATION_REQUIRED_WITH_OPEN_MESSAGE, DO_NOT_CREATE_WITH_OPEN_MESSAGE, VERIFICATION_CODE_REQUIRED_OPEN_MESSAGE].includes(response)) {
          if (response === VERIFICATION_CODE_REQUIRED_OPEN_MESSAGE) {
            setIsMessageOpen(true);
          }
          openDialog();
        } else if (response === VERIFICATION_CODE_REQUIRED) {
          updateMessageState();
          verifyAction();
        } else {
          createCallback(response);
        }
      });
    };

    // Determine if a message should be created based on reContactMessageRule
    const shouldCreateMessage = reContactMessageRule && [CREATE, DONOTCREATE].includes(reContactMessageRule);
    if (shouldCreateMessage) {
      createMessageAction();
      return;
    }
    if (reContactMessageRule === VERIFY) {
      createMessageAction();
      return;
    }
    if (newMessageRule === VERIFY) {
      verifyAction();
    }
  };
  const fetchMessage = (messageId, accessKey, overrideCustConfirmation, overrideAccepted) => {
    fetchCustomerMessage(messageId, accessKey, result => {
      if (!result.hasError) {
        var _result$custClientSco, _result$custClientSco2, _result$custClientSco3;
        if (overrideCustConfirmation === "true") {
          result.custConfirmation = true;
        }
        if (overrideAccepted === "true") {
          result.accepted = true;
          result.acceptedDate = new Date().getTime();
        }
        const {
          footerState
        } = calculatePublicMessageState(result);
        result.footerState = footerState;
        //finish loading
        result.loading = false;
        //clear fields used to derive typing
        result.typing = false;
        result.claimer = "";
        result.claimedAt = null;
        result.hasError = false;
        result.isAccessDenied = false;
        result.isNPSEnabled = ((_result$custClientSco = result.custClientScoreScheme) === null || _result$custClientSco === void 0 ? void 0 : _result$custClientSco.name) === FEEDBACK || ((_result$custClientSco2 = result.custClientScoreScheme) === null || _result$custClientSco2 === void 0 ? void 0 : _result$custClientSco2.name) === EXPERT_FEEDBACK || ((_result$custClientSco3 = result.custClientScoreScheme) === null || _result$custClientSco3 === void 0 ? void 0 : _result$custClientSco3.name) === FURTHER_CONTACT ? true : false;
        setMessage(result);
        addMessageToHistory(result);
        if (ClientContext._customerUtsc && result.customerKey) {
          updateCustomerKey(result.customerKey);
        }
        setLastViewMessage(result);
      } else {
        setMessage({
          ...message,
          hasError: true,
          loading: false,
          isAccessDenied: result.isAccessDenied
        });
        addFailureNotification(result.isAccessDenied ? "Access to this message is restricted to the originator" : "Unable to retrieve message");
      }
    });
  };
  const recalculateMessageState = () => {
    const {
      footerState
    } = calculatePublicMessageState(message);
    updateMessageState(footerState);
  };
  const updateMessageState = state => {
    setMessage({
      ...message,
      footerState: state
    });
  };
  const acceptAnswer = (hasAccepted, callback) => {
    acceptExpertAnswer(message, hasAccepted, result => {
      manageMutationResponse(result, "Unable to accept answer");
      if (typeof callback === "function") {
        callback();
      }
    });
  };
  const submitScore = (score, callback) => {
    submitMessageScore(message, score, result => {
      manageMutationResponse(result, "Unable to submit score");
      if (typeof callback === "function") {
        callback();
      }
    });
  };
  const submitTestimonial = testimonial => {
    submitMessageTestimonial(message, testimonial, result => {
      if (!result.hasError) {
        setMessage(result);
        updateHistoryMessage(result);
      } else {
        addFailureNotification("Unable to submit testimonial");
      }
    });
  };
  const submitNPSNFeedback = (custReview, errMsg, callback) => {
    submitNetPromotorScoreNFeedback(message, custReview, () => {
      var _custReview$custClien, _custReview$custClien2, _message$custClientRe, _ref, _custReview$custClien3, _message$custClientRe2;
      const score = custReview.clientScore;
      const feedback = custReview.feedback;
      const custClientResolved = (_custReview$custClien = custReview.custClientResolved) !== null && _custReview$custClien !== void 0 ? _custReview$custClien : false;
      const custClientExpertScore = (_custReview$custClien2 = custReview.custClientExpertScore) !== null && _custReview$custClien2 !== void 0 ? _custReview$custClien2 : message === null || message === void 0 ? void 0 : (_message$custClientRe = message.custClientReview) === null || _message$custClientRe === void 0 ? void 0 : _message$custClientRe.expertScore;
      const custClientExpertFeedback = (_ref = (_custReview$custClien3 = custReview.custClientExpertFeedback) !== null && _custReview$custClien3 !== void 0 ? _custReview$custClien3 : (_message$custClientRe2 = message.custClientReview) === null || _message$custClientRe2 === void 0 ? void 0 : _message$custClientRe2.expertFeedback) !== null && _ref !== void 0 ? _ref : null;
      const custClientGeneralFeedback = custReview.custClientGeneralFeedback;
      manageMutationResponse({
        ...message,
        custClientReview: {
          score,
          feedback,
          resolved: custClientResolved,
          expertScore: custClientExpertScore,
          expertFeedback: custClientExpertFeedback,
          generalFeedback: custClientGeneralFeedback
        }
      }, errMsg);
      if (typeof callback === "function") {
        callback();
      }
    });
  };
  const reportMessage = callback => {
    reportCustomerMessage(message, result => {
      if (!result.hasError) {
        setMessage(result);
        updateHistoryMessage(result);
        callback();
      } else {
        addFailureNotification("Unable to report message");
      }
    });
  };
  const escalateMessage = () => {
    escalateCustomerMessage(message, result => {
      if (!result.hasError) {
        setMessage(result);
      } else {
        addFailureNotification("Unable to escalate message");
      }
    });
  };
  const manageMutationResponse = (result, failureNotification) => {
    if (!result.hasError) {
      const {
        footerState
      } = calculatePublicMessageState(result);
      const newMessage = {
        ...result,
        footerState
      };
      setMessage({
        ...message,
        ...newMessage
      });
      updateHistoryMessage(newMessage);
    } else {
      addFailureNotification(failureNotification);
    }
  };
  const addDialogue = (body, callback) => {
    if (body.length > 10000) {
      addFailureNotification("length should be <= 10000");
    } else {
      const customer = getCustomerData();
      const tempDialogueId = Math.random();
      const dialogue = {
        dialogueId: tempDialogueId,
        body,
        author: customer.name,
        origin: "customer",
        type: "customer"
      };
      const newDialogues = [...message.dialogues];
      newDialogues.push(dialogue);
      const newTempMessage = {
        ...message
      };
      newTempMessage.dialogues = newDialogues;
      setMessage(newTempMessage);
      callback();
      createDialogue(message, customer, body, result => {
        if (!result.hasError) {
          const newMessage = {
            ...message
          };
          const platformDialogue = result;
          platformDialogue.tempId = tempDialogueId;
          platformDialogue.dialogueId = tempDialogueId;
          newMessage.dialogues.push(platformDialogue);
          setMessage(newMessage);
        } else {
          addFailureNotification("Unable to post reply at this time");
        }
      });
    }
  };
  const addCustomerAttachmentFile = (message, file, body, callback) => {
    addCustomerAttachment(message, file, body, result => {
      if (result.status === 200) {
        fetchMessage(message.messageId, message.accessKey);
        callback(result.statusText);
      } else {
        if (result.error.code === "1038") {
          addFailureNotification("Invalid file. Please try again.");
        } else {
          addFailureNotification("Failed to add attachment. Please try again.");
        }
        callback(result);
      }
    });
  };
  const submitSecureData = (body, callback) => {
    secureData(message, body).then(() => {
      setMessage({
        ...message
      });
      callback();
    }).catch(() => {
      addFailureNotification("Unable to submit secure data at this time");
    });
  };
  const setTyping = (name, userName) => {
    setMessage({
      ...message,
      typing: true,
      claimer: name,
      claimedBy: userName
    });
  };
  const clearTyping = () => {
    setMessage({
      ...message,
      typing: false,
      claimer: "",
      claimedAt: ""
    });
  };
  const clearMessage = () => {
    setMessage(INITIAL_STATE);
  };
  const updateCodeCollector = code => {
    setMessage({
      ...message,
      verificationCode: code
    });
  };
  return {
    message,
    updateMessage,
    createMessage,
    fetchMessage,
    clearMessage,
    acceptAnswer,
    updateMessageState,
    recalculateMessageState,
    submitScore,
    submitTestimonial,
    addDialogue,
    addCustomerAttachmentFile,
    reportMessage,
    escalateMessage,
    setTyping,
    clearTyping,
    submitNPSNFeedback,
    submitSecureData,
    previewImage,
    setPreviewImage,
    generateVerificationCode,
    reContactMessageCreationError,
    updateCodeCollector,
    processMessageBasedOnRule,
    dialogOpen,
    setDialogOpen,
    isMessageOpen,
    setIsMessageOpen
  };
};